<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div>
    <div
      class="rounded-md p-2 border border-solid"
      :class="getInputErrorClass(v.values.$dirty, v.values.$error)"
    >
      <div class="flex">
        <select
          v-if="groupedFilters && showFilterType"
          v-model="attributeKey"
          class="filter-selector-first"
          @change="resetFilter()"
        >
          <optgroup
            v-for="(group, i) in filterGroups"
            :key="i"
            :label="group.name"
          >
            <option
              v-for="attribute in group.attributes"
              :key="attribute.key"
              :value="attribute.key"
            >
              {{ attribute.name }}
            </option>
          </optgroup>
        </select>
        <select
          v-if="!groupedFilters && showFilterType"
          v-model="attributeKey"
          class="filter-selector-first"
          @change="resetFilter()"
        >
          <option
            v-for="attribute in filterAttributes"
            :key="attribute.key"
            :value="attribute.key"
            :disabled="attribute.disabled"
          >
            {{ attribute.name }}
          </option>
        </select>

        <select
          v-if="showFilterOperator"
          v-model="filterOperator"
          class="filter-selector-second"
        >
          <option
            v-for="(operator, o) in operators"
            :key="o"
            :value="operator.value"
          >
            {{ $t(`FILTER.OPERATOR_LABELS.${operator.value}`) }}
          </option>
        </select>

        <div v-if="showUserInput" class="filter__answer--wrap mr-1 flex-grow">
          <input
            v-if="booleanFilterOperator"
            v-model="values"
            type="text"
            class="mb-0"
            :disabled="true"
          />
          <div
            v-else-if="inputType === 'multi_select'"
            class="multiselect-wrap--small"
          >
            <multiselect
              v-model="values"
              track-by="id"
              label="name"
              :placeholder="$t('FILTER.SELECT_PLACEHOLDER')"
              :multiple="true"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="160"
              :options="dropdownValues"
              :allow-empty="true"
            />
          </div>
          <div
            v-else-if="inputType === 'search_select'"
            class="multiselect-wrap--small"
          >
            <multiselect
              v-model="values"
              track-by="id"
              label="name"
              :placeholder="$t('FILTER.SELECT_PLACEHOLDER')"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="160"
              :options="dropdownValues"
              :allow-empty="false"
              :option-height="104"
            />
          </div>
          <div v-else-if="inputType === 'date'" class="multiselect-wrap--small">
            <date-picker
              v-model="values"
              type="date"
              format="DD-MM-YYYY"
              value-type="DD-MM-YYYY"
              class="w-full"
              input-class="mb-0"
            />
          </div>
          <div v-else-if="inputType === 'date_range'" class="w-full">
            <date-picker
              ref="datePicker"
              v-model="dateRange"
              type="date"
              range
              format="DD-MM-YYYY"
              value-type="DD-MM-YYYY"
              :placeholder="$t('FILTER.SELECT_DATE_RANGE')"
              class="w-full"
              input-class="mb-0"
              :confirm="true"
              :clearable="false"
            >
              <template #footer="{ emit }">
                <div
                  class="flex flex-row justify-end gap-2 custom-datepicker-footer"
                >
                  <woot-button
                    color-scheme="secondary"
                    class="clear"
                    size="small"
                    @click="cancelDateSelection(emit)"
                  >
                    {{ $t('FILTER.CANCEL_BUTTON_LABEL') }}
                  </woot-button>
                  <woot-button
                    color-scheme="info"
                    class="clear"
                    size="small"
                    @click="confirmDateSelection(emit)"
                  >
                    {{ $t('FILTER.CONFIRM_BUTTON_LABEL') }}
                  </woot-button>
                </div>
              </template>
            </date-picker>
          </div>
          <input
            v-else
            v-model="values"
            type="text"
            class="mb-0"
            placeholder="Enter value"
          />
        </div>
        <woot-button
          icon="dismiss"
          variant="clear"
          color-scheme="secondary"
          @click="removeFilter"
        />
      </div>
      <p v-if="v.values.$dirty && v.values.$error" class="filter-error">
        {{ $t('FILTER.EMPTY_VALUE_ERROR') }}
      </p>
    </div>

    <div
      v-if="showQueryOperator"
      class="flex items-center justify-center relative my-2.5 mx-0"
    >
      <hr
        class="w-full absolute border-b border-solid border-slate-75 dark:border-slate-800"
      />
      <select
        v-model="query_operator"
        class="bg-white dark:bg-slate-900 mb-0 w-auto relative text-slate-800 dark:text-slate-100 border-slate-75 dark:border-slate-600"
      >
        <option
          v-for="queryOperator in queryOperators"
          :key="queryOperator.value"
          :value="queryOperator.value"
        >
          {{ $t(queryOperator.label) }}
        </option>
      </select>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';

export default {
  components: {
    DatePicker,
  },
  props: {
    value: {
      type: Object,
      default: () => null,
    },
    filterAttributes: {
      type: Array,
      default: () => [],
    },
    inputType: {
      type: String,
      default: 'plain_text',
    },
    dataType: {
      type: String,
      default: 'plain_text',
    },
    operators: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    showQueryOperator: {
      type: Boolean,
      default: false,
    },
    v: {
      type: Object,
      default: () => null,
    },
    showUserInput: {
      type: Boolean,
      default: true,
    },
    groupedFilters: {
      type: Boolean,
      default: false,
    },
    filterGroups: {
      type: Array,
      default: () => [],
    },
    customAttributeType: {
      type: String,
      default: '',
    },
    showFilterOperator: {
      type: Boolean,
      default: true,
    },
    showFilterType: {
      type: Boolean,
      default: true,
    },
    queryOperators: {
      type: Array,
      default: () => [
        { value: 'and', label: 'FILTER.QUERY_DROPDOWN_LABELS.AND' },
        { value: 'or', label: 'FILTER.QUERY_DROPDOWN_LABELS.OR' },
      ],
    },
  },
  computed: {
    attributeKey: {
      get() {
        if (!this.value) return null;
        return this.value.attribute_key;
      },
      set(value) {
        const payload = this.value || {};
        this.$emit('input', { ...payload, attribute_key: value });
      },
    },
    filterOperator: {
      get() {
        if (!this.value) return null;
        return this.value.filter_operator;
      },
      set(value) {
        const payload = this.value || {};
        this.$emit('input', { ...payload, filter_operator: value });
      },
    },
    values: {
      get() {
        if (!this.value) return null;
        return this.value.values;
      },
      set(value) {
        const payload = this.value || {};
        this.$emit('input', { ...payload, values: value, inputType: this.inputType });
      },
    },
    query_operator: {
      get() {
        if (!this.value) return null;
        return this.value.query_operator;
      },
      set(value) {
        const payload = this.value || {};
        this.$emit('input', { ...payload, query_operator: value });
      },
    },
    custom_attribute_type: {
      get() {
        if (!this.customAttributeType) return '';
        return this.customAttributeType;
      },
      set() {
        const payload = this.value || {};
        this.$emit('input', {
          ...payload,
          custom_attribute_type: this.customAttributeType,
        });
      },
    },
    dateRange: {
      get() {
        if (!this.value || !this.value.values) return null;
        if (
          typeof this.value.values === 'object' &&
          this.value.values.from &&
          this.value.values.to
        ) {
          return [this.value.values.from, this.value.values.to];
        }
        return null;
      },
      set(value) {
        const payload = this.value || {};
        if (Array.isArray(value) && value.length === 2) {
          this.$emit('input', {
            ...payload,
            values: { from: value[0], to: value[1] },
            inputType: this.inputType,
          });
        }
      },
    },
    booleanFilterOperator() {
      return ['is_present', 'is_not_present'].includes(this.filterOperator);
    },
  },
  watch: {
    customAttributeType: {
      handler(value) {
        if (
          value === 'conversation_attribute' ||
          value === 'contact_attribute'
        ) {
          // eslint-disable-next-line vue/no-mutating-props
          this.value.custom_attribute_type = this.customAttributeType;
          // eslint-disable-next-line vue/no-mutating-props
        } else this.value.custom_attribute_type = '';
      },
      immediate: true,
    },
    filterOperator: {
      handler(value) {
        if (['is_present', 'is_not_present'].includes(value)) {
          this.values = '';
        }
      },
      immediate: true,
    },
  },
  methods: {
    removeFilter() {
      this.$emit('removeFilter');
    },
    resetFilter() {
      this.$emit('resetFilter');
    },
    getInputErrorClass(isDirty, hasError) {
      return isDirty && hasError
        ? 'bg-red-50 dark:bg-red-800/50 border-red-100 dark:border-red-700/50'
        : 'bg-slate-50 dark:bg-slate-800 border-slate-75 dark:border-slate-700/50';
    },
    confirmDateSelection() {
      if (this.$refs.datePicker) {
        this.$refs.datePicker.handleConfirmDate();
      }
    },
    cancelDateSelection(emit) {
      if (this.$refs.datePicker) {
        const dateRange = this.dateRange.map(date => {
          const [day, month, year] = date.split('-').map(Number);
          return new Date(year, month - 1, day);
        });
        emit(dateRange);
        this.$refs.datePicker.closePopup();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.filter__answer--wrap {
  input {
    @apply bg-white disabled:opacity-75 dark:bg-slate-900 mb-0 text-slate-800 dark:text-slate-100 border-slate-75 dark:border-slate-600;
  }
}
.filter-selector-first {
  @apply bg-white max-w-[30%] dark:bg-slate-900 mb-0 mr-1 text-slate-800 dark:text-slate-100 border-slate-75 dark:border-slate-600;
}

.filter-selector-second {
  @apply bg-white dark:bg-slate-900 max-w-[20%] mb-0 mr-1 text-slate-800 dark:text-slate-100 border-slate-75 dark:border-slate-600
}

.filter-error {
  @apply text-red-500 dark:text-red-200 block my-1 mx-0;
}

.multiselect {
  @apply mb-0;
}

[dir='rtl'] {
  .filter-selector-first,
  .filter-selector-second {
    @apply pl-6 pr-2;
    background-position: left -1rem center;
  }
}
</style>
